import React, { useMemo } from 'react';
import { Animated, Platform, Pressable, ScrollView, Text, View } from 'react-native';
import {
  CheckCircle,
  Flag,
  Lightbulb,
  Medal,
  Star,
  Swords,
  Trophy,
  Zap,
  type LucideIcon,
} from 'lucide-react-native';
import { useTheme } from '../../../src/theme/useTheme';
import { WEB_HOVER_TRANSITION_STYLE } from '../../../src/shared/constants/animations';
import { WORLD_CUP_WELCOME } from '../../../src/worldcup/worldcup.constants';
import { WORLD_CUP_WELCOME_POINTS_STAGGER_BLOCK_COUNT } from './WorldCupWelcomeScreen.constants';
import {
  WELCOME_POINTS_TIMELINE_STAGES,
  type WelcomePointsTimelineIconId,
} from './WelcomePointsPhase.constants';
import {
  useWorldCupWelcomeStaggerEntrance,
  staggerStyle,
} from './useWorldCupWelcomeStaggerEntrance';
import { getWelcomePointsPhaseStyles } from './WelcomePointsPhase.styles';

const TIMELINE_STAGE_ICONS: Record<WelcomePointsTimelineIconId, LucideIcon> = {
  flag: Flag,
  swords: Swords,
  trophy: Trophy,
  medal: Medal,
};

export interface WelcomePointsPhaseProps {
  phase: number;
  isExiting: boolean;
  scrollContentPaddingTop: number;
  onNext: () => void;
  testID?: string;
}

export const WelcomePointsPhase: React.FC<WelcomePointsPhaseProps> = ({
  phase,
  isExiting,
  scrollContentPaddingTop,
  onNext,
  testID,
}) => {
  const theme = useTheme();
  const styles = useMemo(
    () => getWelcomePointsPhaseStyles(theme, { scrollContentPaddingTop }),
    [theme, scrollContentPaddingTop],
  );
  const stagger = useWorldCupWelcomeStaggerEntrance(
    phase,
    2,
    WORLD_CUP_WELCOME_POINTS_STAGGER_BLOCK_COUNT,
  );

  const colExact = WORLD_CUP_WELCOME.POINTS_TABLE.COL_EXACT;
  const colOutcome = WORLD_CUP_WELCOME.POINTS_TABLE.COL_OUTCOME;

  return (
    <View style={styles.phaseRoot}>
      <ScrollView
        style={[styles.scrollPhase, Platform.OS === 'web' && styles.scrollPhaseWeb]}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
        bounces={Platform.OS !== 'web'}
        testID={testID}
      >
        <Animated.View style={staggerStyle(stagger[0])}>
          <Text style={styles.phaseLabel}>{WORLD_CUP_WELCOME.POINTS_TABLE.LABEL}</Text>
          <Text style={styles.phaseTitle}>{WORLD_CUP_WELCOME.POINTS_TABLE.TITLE}</Text>
        </Animated.View>

        <Animated.View style={staggerStyle(stagger[1])}>
          <View style={styles.timelineContainer}>
            <View style={styles.timelineLine} />
            {WELCOME_POINTS_TIMELINE_STAGES.map((stage) => {
              const isLeft = stage.align === 'left';
              const StageIcon = TIMELINE_STAGE_ICONS[stage.icon];
              const card = (
                <View style={styles.stageCard}>
                  <View style={styles.stageCardIconWrap}>
                    <StageIcon size={20} color={theme.primaryDark} strokeWidth={2.2} />
                  </View>
                  <View style={styles.stageCardBody}>
                    <Text style={styles.stageCardTitle}>{stage.stage}</Text>
                    {stage.compact ? (
                      <View style={styles.pointsRowCompact}>
                        <View style={styles.pointsStatGroup}>
                          <Star size={12} color={theme.success} strokeWidth={2.4} />
                          <Text style={styles.pointsText}>{stage.hit}</Text>
                        </View>
                        <View style={styles.pointsStatGroup}>
                          <CheckCircle size={12} color={theme.info} strokeWidth={2.4} />
                          <Text style={styles.pointsText}>{stage.result}</Text>
                        </View>
                      </View>
                    ) : (
                      <>
                        <View style={styles.pointsLine}>
                          <Star size={12} color={theme.success} strokeWidth={2.4} />
                          <Text style={styles.pointsText}>
                            {stage.hit} {colExact}
                          </Text>
                        </View>
                        <View style={styles.pointsLine}>
                          <CheckCircle size={12} color={theme.info} strokeWidth={2.4} />
                          <Text style={styles.pointsText}>
                            {stage.result} {colOutcome}
                          </Text>
                        </View>
                        {stage.goldenBall && (
                          <View style={[styles.pointsLine, styles.pointsLineLast]}>
                            <Zap size={12} color={theme.accentDark} strokeWidth={2.4} />
                            <Text style={styles.pointsTextGolden}>
                              {WORLD_CUP_WELCOME.POINTS_TABLE.COL_GOLDEN_BALL}
                            </Text>
                          </View>
                        )}
                      </>
                    )}
                  </View>
                </View>
              );

              return (
                <View key={stage.id} style={styles.stageRow}>
                  <View style={[styles.halfColumn, styles.halfColumnLeft]}>
                    {isLeft ? card : null}
                  </View>
                  <View style={styles.centerTrack}>
                    <View style={styles.timelineDot} />
                  </View>
                  <View style={[styles.halfColumn, styles.halfColumnRight]}>
                    {!isLeft ? card : null}
                  </View>
                </View>
              );
            })}
          </View>
        </Animated.View>

        <Animated.View style={staggerStyle(stagger[2])}>
          <View style={styles.proTipsSection}>
            <View style={styles.proTipsHeader}>
              <Lightbulb size={20} color={theme.accentDark} strokeWidth={2.2} />
              <Text style={styles.proTipsTitle}>
                {WORLD_CUP_WELCOME.POINTS_TABLE.PRO_TIPS_TITLE}
              </Text>
            </View>

            <View style={styles.proTipCard}>
              <View style={styles.proTipIconsColumn}>
                <Zap size={18} color={theme.accentDark} strokeWidth={2.2} />
              </View>
              <Text style={styles.proTipText}>
                <Text style={styles.proTipBold}>
                  {WORLD_CUP_WELCOME.POINTS_TABLE.DOUBLE_POINTS_TITLE}
                  {': '}
                </Text>
                {WORLD_CUP_WELCOME.POINTS_TABLE.DOUBLE_POINTS_BODY}
              </Text>
            </View>
          </View>
        </Animated.View>

        <Animated.View style={staggerStyle(stagger[3])}>
          <View style={styles.proTipCard}>
            <View style={styles.proTipIconsColumn}>
              <Trophy size={18} color={theme.primary} strokeWidth={2.2} />
            </View>
            <Text style={styles.proTipText}>
              <Text style={styles.proTipBold}>
                {WORLD_CUP_WELCOME.POINTS_TABLE.TOURNAMENT_PICKS_TITLE}
                {': '}
              </Text>
              {WORLD_CUP_WELCOME.POINTS_TABLE.TOURNAMENT_PICKS_BODY}
            </Text>
          </View>
        </Animated.View>
      </ScrollView>

      <Animated.View style={[styles.footer, staggerStyle(stagger[4])]}>
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={WORLD_CUP_WELCOME.CTA_ENTER_APP}
          disabled={isExiting}
          onPress={onNext}
          style={({ pressed, hovered }) => [
            styles.ctaButton,
            Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
            pressed && !isExiting && styles.ctaButtonPressed,
            hovered && Platform.OS === 'web' && styles.ctaButtonHovered,
          ]}
        >
          <Text style={styles.ctaButtonLabel}>{WORLD_CUP_WELCOME.CTA_ENTER_APP}</Text>
        </Pressable>
      </Animated.View>
    </View>
  );
};
